import React, { useEffect, useState } from "react";
import "./SettlementReport.css";
import { useSelector } from "react-redux";
import { getMerchantSettlement } from "../../../config/LiveApi";
import { toast } from "react-toastify";
import { FcCheckmark, FcMoneyTransfer } from "react-icons/fc";
import Modal from "./ModalPaid";
import ExcelDownloader from "./ExcelDownloader";
import DateFilter from "../../../components/common/DateFilter";

import DataTable from "../../../components/common/DataTable/DataTable";
import DateCell from "../../../components/common/TableCells/DateCell";
import StatusCell from "../../../components/common/TableCells/StatusCell";

const SettlementTable = () => {
  const token = useSelector((store) => store.userReducer.token);
  const role = useSelector((store) => store.userReducer.role);

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState({
    open: false,
    data: null,
  });

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getSettlementData = async (start = startDate, end = endDate) => {
    setLoading(true);

    try {
      const response = await getMerchantSettlement(token, start, end);

      toast.success(response.message || "Data fetched successfully!", {
        autoClose: 1000,
      });

      setTableData(response.data?.data || []);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && startDate && endDate) {
      getSettlementData();
    }
  }, [token, startDate, endDate]);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const isAdminView = role === "admin" || role === "subAdmin";

  const columns = [
    ...(!isAdminView
      ? [
          {
            key: "merchant",
            header: "Merchant Name",
            render: (row) => row.alias || row.userId,
          },
        ]
      : []),

    {
      key: "settlementAmount",
      header: "Total Settlement Amount",
      wrapHeader: true,
      render: (row) => `₹ ${Number(row?.settlementAmount || 0).toFixed(2)}`,
    },
    {
      key: "walletAmount",
      header: "Wallet Amount",
      render: (row) =>
        `₹ ${row?.status ? Number(row?.paidAmount || 0).toFixed(2) : "0.00"}`,
    },
    {
      key: "rrAmount",
      header: "Rolling Reserve Amount",
      wrapHeader: true,
      render: (row) => `₹ ${Number(row?.rrAmount || 0).toFixed(2)}`,
    },

    ...(!isAdminView
      ? [
          {
            key: "fundDifference",
            header: "Fund Difference",
            render: (row) =>
              `₹ ${Number(
                (row?.settlementAmount || 0) - (row?.paidAmount || 0),
              ).toFixed(2)}`,
          },
        ]
      : []),
    {
      key: "settlementTimeRange",
      header: "Settlement Time Range",
      wrapHeader: true,
      render: (row) => {
        const range = row?.settlementTimeRange;

        if (!range) return "N/A";

        const match = range.match(
          /^(\d{4}-\d{2}-\d{2}T[\d:.]+Z)-(\d{4}-\d{2}-\d{2}T[\d:.]+Z)$/,
        );

        if (!match) return range;

        const start = new Date(match[1]);
        const end = new Date(match[2]);

        return `${start.toLocaleString("en-GB")} - ${end.toLocaleString(
          "en-GB",
        )}`;
      },
    },
    {
      key: "settledDate",
      header: "Settled Date",
      render: (row) => <DateCell date={row.settledDate} />,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <StatusCell status={row?.status ? "SUCCESS" : "PENDING"} />
      ),
    },

    ...(!isAdminView
      ? [
          {
            key: "payAmount",
            header: "Pay Amount",
            render: (row) => (
              <FcMoneyTransfer
                size={25}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setIsModalOpen({
                    open: true,
                    data: row,
                    type: "manual",
                  })
                }
              />
            ),
          },
          {
            key: "directPay",
            header: "Direct Pay",
            render: (row) => (
              <FcCheckmark
                size={25}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setIsModalOpen({
                    open: true,
                    data: row,
                    type: "direct",
                  })
                }
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="provider-settlement-report-container">
        <div className="provider-table-head">
          <div className="provider-settlement-report-title">
            Merchant Settlement
          </div>

          <ExcelDownloader
            data={tableData}
            role={role}
            fileName="Merchant_Settlement"
          />
        </div>

        <DateFilter onDateChange={handleDateChange} />

        <DataTable
          columns={columns}
          data={tableData}
          loading={loading}
          emptyMessage="No Data Found"
          rowClassName={(row) => (row?.status <= 0 ? "highlight-row-red" : "")}
        />
      </div>

      {isModalOpen.open && (
        <Modal
          onClose={() =>
            setIsModalOpen({
              open: false,
              data: null,
            })
          }
          reportData={isModalOpen.data}
          paidAmount={isModalOpen.paidAmount}
          type={isModalOpen.type}
          refreshTableData={getSettlementData}
          name={isModalOpen.data?.name}
        />
      )}
    </>
  );
};

export default SettlementTable;
